import {
    ClaimWorkflowConfiguration,
    ClaimWorkflowConfigurationOption,
    ClaimWorkflowConfigurationOptions,
} from '../../api';
import { canEditWorkflowOption, isRetiredWorkflowOption } from './worker-configuration.permissions';
import { WorkerConfigurationApiMode } from './worker-configuration.api';
import { stableStringify } from './stable-stringify';

/** A single workflow configuration option value. */
export type WorkflowConfigValue = boolean | number | string | string[];

/** True when a configuration value is unset (missing from config or explicitly cleared). */
export function isUnsetConfigValue(value: WorkflowConfigValue | null | undefined): boolean {
    return value === null || value === undefined;
}

/**
 * Digit-only text for the numeric editor. Not the saved JSON type —
 * {@link toNumericConfigValue} produces the number the backend expects.
 */
export function toNumericConfigString(value: unknown): string | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
        const digits = String(Math.trunc(value)).replace(/\D/g, '');
        return digits === '' ? undefined : digits;
    }

    if (typeof value === 'string') {
        const digits = value.replace(/\D/g, '');
        return digits === '' ? undefined : digits;
    }

    return undefined;
}

/** Leading optional-minus integer token. Must stay aligned with compare-normalization. */
const INTEGER_CONFIG_TOKEN = /^-?\d+/;

/**
 * Parses a config value as a JSON number for the backend.
 * `integer` truncates; `number` keeps a finite float.
 * Integer strings keep their sign (`-5` stays `-5`); stripping non-digits would flip it to `5`.
 */
export function toNumericConfigValue(value: unknown, valueType?: string): number | undefined {
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            return undefined;
        }
        return valueType === 'number' ? value : Math.trunc(value);
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return undefined;
        }
        if (valueType === 'number') {
            const parsed = Number(trimmed);
            return Number.isFinite(parsed) ? parsed : undefined;
        }
        const match = INTEGER_CONFIG_TOKEN.exec(trimmed);
        if (!match) {
            return undefined;
        }
        const parsed = Number.parseInt(match[0], 10);
        return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
}

/** Configured values for one workflow status. */
export type WorkflowStatusConfig = Record<string, WorkflowConfigValue>;

/** Full workflow config keyed by status. */
export type WorkflowConfigMap = Record<string, WorkflowStatusConfig>;

/**
 * Claim workflow configuration with accurate value types.
 * The generated API model types all config values as boolean only.
 */
export interface WorkerWorkflowConfiguration {
    createDate?: string;
    active?: boolean;
    config?: WorkflowConfigMap;
    /** Set by the backend from the session token; do not send on create. */
    createdBy?: string;
}

/** Deep-clones a workflow config map. */
export function cloneWorkflowConfigMap(config?: WorkflowConfigMap): WorkflowConfigMap {
    return JSON.parse(JSON.stringify(config ?? {})) as WorkflowConfigMap;
}

/** Returns a new config map with one option set or cleared (removed when unset). */
export function setWorkflowConfigValue(
    config: WorkflowConfigMap,
    status: string,
    key: string,
    value: WorkflowConfigValue | null | undefined,
): WorkflowConfigMap {
    const next = cloneWorkflowConfigMap(config);

    if (!next[status]) {
        next[status] = {};
    }

    if (value === null || value === undefined) {
        delete next[status][key];
        if (Object.keys(next[status]).length === 0) {
            delete next[status];
        }
        return next;
    }

    next[status][key] = value;
    return next;
}

/** True when two workflow config maps are deeply equal. */
export function workflowConfigsEqual(left: WorkflowConfigMap, right: WorkflowConfigMap): boolean {
    return (
        stableStringify(normalizeWorkflowConfigMapForCompare(left)) ===
        stableStringify(normalizeWorkflowConfigMapForCompare(right))
    );
}

/** True when a status has at least one configured option value. */
export function statusHasConfiguredOptions(statusConfig?: WorkflowStatusConfig): boolean {
    return Boolean(statusConfig && Object.keys(statusConfig).length > 0);
}

function normalizeWorkflowConfigValueForCompare(value: WorkflowConfigValue): WorkflowConfigValue {
    const numeric = toNumericConfigValue(value, typeof value === 'number' && !Number.isInteger(value) ? 'number' : 'integer');
    if (typeof value === 'number' && Number.isFinite(value)) {
        return numeric ?? value;
    }

    if (typeof value === 'string' && numeric !== undefined && /^-?\d+(\.\d+)?$/.test(value.trim())) {
        return numeric;
    }

    return value;
}

function normalizeWorkflowConfigMapForCompare(config: WorkflowConfigMap): WorkflowConfigMap {
    const next: WorkflowConfigMap = {};

    for (const [status, statusConfig] of Object.entries(config)) {
        const normalizedStatus: WorkflowStatusConfig = {};
        for (const [key, value] of Object.entries(statusConfig)) {
            normalizedStatus[key] = normalizeWorkflowConfigValueForCompare(value);
        }
        next[status] = normalizedStatus;
    }

    return next;
}

/**
 * Parses the option metadata `default` into a stored config value.
 * Returns undefined when the options endpoint did not provide a default.
 * Integer/number defaults are stored as JSON numbers.
 */
export function parseOptionDefaultValue(
    option: Pick<ClaimWorkflowConfigurationOption, 'key' | '_default' | 'valueType'>,
): WorkflowConfigValue | undefined {
    const raw = option._default as unknown;
    if (raw === undefined || raw === null) {
        return undefined;
    }

    const valueType = option.valueType ?? '';

    if (valueType === 'boolean') {
        if (raw === true || raw === 'true') {
            return true;
        }

        if (raw === false || raw === 'false') {
            return false;
        }

        return undefined;
    }

    if (valueType === 'integer' || valueType === 'number') {
        return toNumericConfigValue(raw, valueType);
    }

    if (typeof raw === 'boolean' || typeof raw === 'number' || typeof raw === 'string') {
        return raw;
    }

    return undefined;
}

/**
 * Fills unset option keys from the options-endpoint defaults.
 * Already configured values are left unchanged.
 * List-typed options (including automation `rules`) are skipped — they are owned by specialized editors.
 * Retired options (e.g. the worker-level `group`) are skipped so their defaults cannot re-enter the draft.
 * Support-only options are skipped in provider mode so providers never seed or write them.
 */
export function applyWorkflowOptionDefaults(
    config: WorkflowConfigMap,
    optionsList: ClaimWorkflowConfigurationOptions[],
    apiMode: WorkerConfigurationApiMode = 'support',
): WorkflowConfigMap {
    let next = cloneWorkflowConfigMap(config);

    for (const statusOptions of optionsList) {
        const status = statusOptions.workflowStatus?.trim();
        if (!status) {
            continue;
        }

        for (const option of statusOptions.options ?? []) {
            const key = option.key?.trim();
            if (!key || option.valueType === 'list' || isRetiredWorkflowOption(status, key)) {
                continue;
            }

            if (!canEditWorkflowOption(apiMode, status, key)) {
                continue;
            }

            if (!isUnsetConfigValue(next[status]?.[key])) {
                continue;
            }

            const defaultValue = parseOptionDefaultValue(option);
            if (defaultValue === undefined) {
                continue;
            }

            next = setWorkflowConfigValue(next, status, key, defaultValue);
        }
    }

    return next;
}

/**
 * Rewrites integer/number option values to JSON numbers using catalog `valueType`.
 * Leaves booleans, lists, and strings unchanged.
 */
export function coerceNumericWorkflowConfigValues(
    config: WorkflowConfigMap,
    optionsList: ClaimWorkflowConfigurationOptions[],
): WorkflowConfigMap {
    const next = cloneWorkflowConfigMap(config);

    for (const statusOptions of optionsList) {
        const status = statusOptions.workflowStatus?.trim();
        if (!status || !next[status]) {
            continue;
        }

        for (const option of statusOptions.options ?? []) {
            const key = option.key?.trim();
            if (!key || (option.valueType !== 'integer' && option.valueType !== 'number')) {
                continue;
            }

            const current = next[status][key];
            if (isUnsetConfigValue(current)) {
                continue;
            }

            const numeric = toNumericConfigValue(current, option.valueType);
            if (numeric !== undefined) {
                next[status][key] = numeric;
            }
        }
    }

    return next;
}

/** Maps the API response to the locally accurate configuration shape. */
export function parseWorkerWorkflowConfiguration(
    config: ClaimWorkflowConfiguration,
): WorkerWorkflowConfiguration {
    return {
        createDate: config.createDate,
        active: config.active,
        createdBy: config.createdBy,
        config: config.config as unknown as Record<string, WorkflowStatusConfig> | undefined,
    };
}
